import { Box } from '@mui/material'
import { ReactElement, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useAppSelector } from '../../../app/hooks'
import { useGetLocationsQuery } from '../../../app/redux-fetch/apiCoreQuery'
import { useGetCustomersQuery } from '../../../app/redux-fetch/apiQuery'
import ActionButton from '../../../components/Buttons/ActionButton'
import ConditionalRender from '../../../components/ConditionalRender/ConditionalRender'
import FormInput from '../../../components/Forms/FormInput'
import FormSelect from '../../../components/Forms/FormSelect'
import SwitchWithLabels from '../../../components/Switches/SwitchWithLabels'
import { SectionTitleTypography } from '../../../components/Typographies/styles'
import { LocationType } from '../../../types/common'
import { fetchLoggedInUserSelector } from '../../selectors'
import { ILoggedInUser } from '../../types'
import { useLocationFormContext } from '../hooks/useLocationFormContext'
import messages from '../messages'

type MainInfoProps = {
    isDisabled?: boolean
    type: 'create' | 'view' | 'edit'
    isTenantUser?: boolean
    isCustomerDisabled?: boolean
    isOrderLocation?: boolean
    locationTypeId: number
    isSelectLocationType?: boolean
    canBeParented?: boolean
}

const MainInfo = ({ isDisabled, type, isTenantUser, isCustomerDisabled, locationTypeId, canBeParented = false, isOrderLocation = false, isSelectLocationType = false }: MainInfoProps): ReactElement<any, any> => {
    const { control, setValue, watch } = useLocationFormContext()
    const { formatMessage } = useIntl()

    const loggedInUser = useAppSelector(fetchLoggedInUserSelector.data) || ({} as ILoggedInUser)
    const { tenantId } = loggedInUser

    const { data: customersResult = [] } = useGetCustomersQuery(tenantId ?? -1)

    const values = watch()

    const customers = useMemo(() => {
        return isOrderLocation ? customersResult.filter(c => c.id === values.customerId).map((customer) => ({
            label: customer.name,
            value: customer.id,
        })) : customersResult.map((customer) => ({
            label: customer.name,
            value: customer.id,
        }))
    }, [customersResult, isOrderLocation, values.customerId])

    const isCustomerIdNotEditable = isCustomerDisabled
    const isCustomerDropdownEnabled = isTenantUser
    const isNew = type === 'create'

    const { data: locations = [] } = useGetLocationsQuery(
        {
            tenantId: tenantId ?? -1,
        }
    )

    const locationOptions = useMemo(() => {
        return locations.filter(location => {
            if (location.id === values.id) return false;

            if (locationTypeId === LocationType.Terminal) {
                return location.locationTypeId === LocationType.Airport ||
                    location.locationTypeId === LocationType.Seaport;
            }

            if (locationTypeId === LocationType.Standard || locationTypeId === LocationType.FuelStation) {
                return location.locationTypeId !== LocationType.Shipper;
            }

            return true;
        }).map((location) => ({
            value: location.id ?? -1,
            label: location.name ?? ''
        }));
    }, [locations, values.id, locationTypeId])

    const locationTypeOptions = useMemo(() => [
        { value: LocationType.Standard, label: formatMessage(messages.standard) },
        { value: LocationType.Terminal, label: formatMessage(messages.terminal) },
        { value: LocationType.Seaport, label: formatMessage(messages.seaport) },
        { value: LocationType.Airport, label: formatMessage(messages.airport) },
        { value: LocationType.FuelStation, label: formatMessage(messages.fuelStation) }
    ], [formatMessage]);

    return (
        <Box sx={{ mb: '16px', width: '75%' }}>
            <Box sx={{ mb: '16px' }}>
                <SectionTitleTypography>{formatMessage(messages.mainInfo)}</SectionTitleTypography>
            </Box>

            <FormInput
                name="name"
                label={formatMessage(messages.name)}
                required
                disabled={isDisabled || (values.systemDefined && !isNew)}
                maxLength={100}
            />
            <ConditionalRender condition={isSelectLocationType}>
                <FormSelect
                    name="locationTypeId"
                    label={formatMessage(messages.locationType)}
                    options={locationTypeOptions}
                    disabled={isDisabled || (type !== 'create')}
                    onChange={(event) => {
                        const newValue = Number(event.target.value)
                        setValue('customerSelectable', undefined)
                        setValue('locationTypeId', newValue)
                    }}
                />
            </ConditionalRender>
            <ConditionalRender condition={isCustomerDropdownEnabled}>
                <FormSelect
                    name="customerId"
                    label={formatMessage(messages.customerLabel)}
                    options={customers}
                    allowClear={true}
                    disabled={isDisabled || isCustomerIdNotEditable || (values.systemDefined && !isNew)}
                    onChange={(event) => {
                        const newValue = event.target.value === '' ? null : Number(event.target.value)
                        setValue('customerSelectable', undefined)
                        setValue('customerId', newValue)
                    }}
                    actionLabel={
                        !isDisabled && !isCustomerIdNotEditable && !(values.systemDefined && !isNew) && values.customerId ? (
                            <ActionButton
                                onClick={() => {
                                    setValue('customerId', null)
                                    setValue('customerSelectable', undefined)
                                }}
                                label={formatMessage(messages.clearSelection)}
                            />
                        ) : undefined
                    }
                />
            </ConditionalRender>

            <ConditionalRender condition={canBeParented}>
                <FormSelect
                    name="locationParentId"
                    label={formatMessage(messages.parentLocation)}
                    options={locationOptions}
                    disabled={isDisabled || isCustomerIdNotEditable || (values.systemDefined && !isNew)}
                    onChange={(event) => {
                        const newValue = Number(event.target.value)
                        setValue('locationParentId', newValue)
                    }}
                />
            </ConditionalRender>

            <ConditionalRender condition={!values.customerId}>
                <Box sx={{ mt: 2 }}>
                    <SwitchWithLabels
                        value={values.customerSelectable}
                        onChange={(value) => setValue('customerSelectable', value)}
                        labelAfter="Customer Selectable"
                        disabled={isDisabled}
                    />
                </Box>
            </ConditionalRender>

            <Box sx={{ mt: 2 }}>
                <SwitchWithLabels
                    value={values.enabled ?? true}
                    onChange={(value) => setValue('enabled', value)}
                    labelAfter={formatMessage(messages.enabled)}
                    disabled={isDisabled}
                />
            </Box>
        </Box>
    )
}

export default MainInfo
